export const VB_W = 100;
export const VB_H = 40;
export const VB_PAD = 3.5;

/**
 * @param {string} dateStr YYYY-MM-DD
 * @returns {number} epoch ms at UTC midnight
 */
export function tOf(dateStr) {
	return Date.parse(dateStr + 'T00:00:00Z');
}

/**
 * @param {number} x
 * @param {number} y
 * @returns {string}
 */
export function pt(x, y) {
	return `${x.toFixed(2)} ${y.toFixed(2)}`;
}

/**
 * Zero-baseline scale: min 0 (or the series minimum when negative).
 * @param {number[]} vals
 * @returns {{ min: number, range: number }}
 */
export function normalize(vals) {
	const min = Math.min(0, ...vals);
	const max = Math.max(...vals);
	return { min, range: max - min || 1 };
}

/**
 * @param {number} i
 * @param {number} n
 * @returns {number}
 */
export function xPos(i, n) {
	return (i / (n - 1)) * VB_W;
}

/**
 * @param {number} v
 * @param {number} min
 * @param {number} range
 * @returns {number}
 */
export function yPos(v, min, range) {
	return VB_PAD + (1 - (v - min) / range) * (VB_H - 2 * VB_PAD);
}

/**
 * @param {number[]} vals
 * @param {number} min
 * @param {number} range
 * @returns {string}
 */
export function toPath(vals, min, range) {
	const n = vals.length;
	return vals.map((v, i) => (i ? 'L' : 'M') + pt(xPos(i, n), yPos(v, min, range))).join(' ');
}

/**
 * Interpolate a value at a fractional index (linear between samples).
 * @param {number[]} vals
 * @param {number} idx
 * @returns {number}
 */
export function interpAt(vals, idx) {
	const i = Math.min(Math.floor(idx), vals.length - 1);
	const j = Math.min(i + 1, vals.length - 1);
	const f = idx - i;
	return vals[i] + (vals[j] - vals[i]) * f;
}

/**
 * Fractional x-boundaries (in index space) where the two series cross, plus
 * the outer edges, so adjacent bands meet exactly at crossings — no gaps.
 * @param {number[]} balance
 * @param {number[]} principal
 * @returns {number[]}
 */
export function crossingBounds(balance, principal) {
	const bounds = [0];
	for (let i = 0; i < balance.length - 1; i++) {
		const a = balance[i] - principal[i];
		const b = balance[i + 1] - principal[i + 1];
		if (a === 0) {
			bounds.push(i);
		} else if ((a < 0 && b > 0) || (a > 0 && b < 0)) {
			bounds.push(i + a / (a - b));
		}
	}
	bounds.push(balance.length - 1);
	return bounds;
}

/**
 * Polygon for the region between the two curves over [b0, b1].
 * @param {number} b0
 * @param {number} b1
 * @param {boolean} profit
 * @param {{ balance: number[], principal: number[], min: number, range: number, n: number }} ctx
 * @returns {string}
 */
export function buildBand(b0, b1, profit, ctx) {
	const { balance, principal, min, range, n } = ctx;
	const upper = profit ? balance : principal;
	const lower = profit ? principal : balance;
	const parts = [];
	const push = (vals, i) => parts.push(pt(xPos(i, n), yPos(interpAt(vals, i), min, range)));
	const s0 = Math.ceil(b0);
	const s1 = Math.floor(b1);
	push(upper, b0);
	for (let i = s0; i <= s1; i++) {
		if (i === b0 || i === b1) { continue; }
		push(upper, i);
	}
	push(upper, b1);
	push(lower, b1);
	for (let i = s1; i >= s0; i--) {
		if (i === b0 || i === b1) { continue; }
		push(lower, i);
	}
	push(lower, b0);
	return parts.map((p, j) => (j ? 'L' : 'M') + p).join(' ') + ' Z';
}

/**
 * Build polygon paths for regions where values fall below zero.
 * @param {number[]} vals
 * @param {number} min
 * @param {number} range
 * @param {number} zy — y-coordinate of zero line
 * @returns {string}
 */
export function belowZeroPath(vals, min, range, zy) {
	const n = vals.length;
	let d = '';
	for (let i = 0; i < n; i++) {
		if (vals[i] < 0) {
			const s = i;
			while (i < n && vals[i] < 0) { i++; }
			const e = i - 1;
			d += `M${pt(xPos(s, n), zy)}`;
			for (let j = s; j <= e; j++) {
				d += ` L${pt(xPos(j, n), yPos(vals[j], min, range))}`;
			}
			d += ` L${pt(xPos(e, n), zy)} Z `;
		}
	}
	return d.trim();
}
